import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { mono, sans, serif } from '../fonts.js';
import { labelFromSlug, resolvePitch } from './copy-deck.js';
import { formatCount, formatLimit, formatMoneyMinor, formatPrice, pageLoadsRatioClause, pickPrice, } from './format.js';
import { PlanCta } from './PlanCta.js';
import { Tooltip } from './Tooltip.js';
import { CheckMark, EarlyTag } from './icons.js';
/** Lowercase the first letter for mid-sentence use, leaving acronyms
 *  such as "AI" alone. */
export function lowerFirst(label) {
    if (/^[A-Z]{2}/.test(label))
        return label;
    // Product names keep their capital.
    if (/^(Claude|ChatGPT|Slack|Shopify|Cloudflare|WordPress|Stripe)\b/.test(label))
        return label;
    return label.charAt(0).toLowerCase() + label.slice(1);
}
/** Return the headline limit rows to show on a card. We render in the
 *  dimension order the copy deck lays out in `compareSections`, and
 *  cap at five. */
function headlineRows(plan, copy) {
    const seen = new Set();
    const ordered = [
        'page_loads_monthly',
        'orders_monthly',
        'assistant_requests_monthly',
        'retention_days',
    ];
    const rows = [];
    for (const d of ordered) {
        const row = plan.limits.find((l) => l.dimension === d);
        if (row === undefined)
            continue;
        seen.add(d);
        const deck = copy.dimensions[d];
        rows.push({
            dimension: d,
            label: deck?.label ?? labelFromSlug(d),
            tooltip: deck?.tooltip ?? '',
            value: formatLimit(row),
        });
    }
    // Also surface any dimension the plan carries that isn't in the
    // canonical order — the neutral label falls back from the slug so a
    // staff-added dimension never surfaces blank.
    for (const l of plan.limits) {
        if (seen.has(l.dimension))
            continue;
        const deck = copy.dimensions[l.dimension];
        rows.push({
            dimension: l.dimension,
            label: deck?.label ?? labelFromSlug(l.dimension),
            tooltip: deck?.tooltip ?? '',
            value: formatLimit(l),
        });
    }
    return rows.slice(0, 5);
}
export function PricingCards({ plans, theme, interval, copy, ctaHref, showDraftBadges, monthsFreeOnYearly = 2, featuredIndex, }) {
    const resolvedFeatured = featuredIndex ?? (plans.length >= 3 ? 1 : -1);
    return (_jsx("div", { className: "pr-cards", style: {
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            columnGap: 16,
            rowGap: 14,
        }, children: plans.map((plan, i) => {
            const featured = i === resolvedFeatured;
            const price = pickPrice(plan.prices, interval);
            const monthlyPrice = pickPrice(plan.prices, 'monthly');
            const yearlyPrice = pickPrice(plan.prices, 'yearly');
            const headline = headlineRows(plan, copy);
            const planCopy = copy.plans[plan.slug];
            let pitch = resolvePitch(plan.slug, plan.description, copy);
            // The top tier's pitch gets a "N times the room" clause derived
            // from the catalog page-loads ratio when both prior and top
            // plans have finite limits and the ratio is a whole number ≥ 2.
            // Drops the clause otherwise; the founder can tune the ratio by
            // editing the limit in ops.
            const isTopTier = i === plans.length - 1 && plans.length >= 3;
            if (isTopTier && pitch !== null) {
                // Prefer the featured plan (usually the middle, "Protect") as
                // the base for the ratio; fall back to any earlier paid plan.
                const basePlan = plans[resolvedFeatured >= 0 ? resolvedFeatured : i - 1]
                    ?? plans[i - 1];
                const clause = pageLoadsRatioClause(basePlan, plan);
                if (clause !== null && !/\btimes\b/i.test(pitch)) {
                    pitch = pitch.replace(/\.?$/, '') + `, ${clause}.`;
                }
            }
            const who = planCopy?.who;
            const req = planCopy?.req;
            const note = planCopy?.note;
            const firstPlan = i === 0;
            const leadKicker = firstPlan ? 'Includes' : `Everything in ${plans[i - 1]?.name ?? 'Free'}, plus`;
            // First card: the features every plan has, then its own
            // catalog features. Later cards: only the catalog features the
            // previous plan lacks, under "Everything in X, plus".
            const prevFeatures = new Set(firstPlan ? [] : (plans[i - 1]?.features ?? []));
            const baseLines = firstPlan
                ? (copy.baseFeatures ?? []).map((b, k) => ({
                    slug: `base-${k}`,
                    label: b.label,
                    tooltip: b.tooltip ?? '',
                    early: b.early === true,
                }))
                : [];
            const featureLines = [
                ...baseLines,
                ...plan.features
                    .filter((slug) => !prevFeatures.has(slug))
                    .map((slug) => {
                    const row = copy.features[slug];
                    if (row === undefined)
                        return null;
                    return { slug, label: row.label, tooltip: row.tooltip ?? '', early: false };
                })
                    .filter((v) => v !== null),
            ];
            const displayedPrice = price ?? monthlyPrice;
            const isFree = (displayedPrice?.amount_minor ?? 0) === 0;
            // Per-month figure shown on the card: when the active interval
            // is yearly, show the yearly amount split across (12 - monthsFree)
            // months to match the "two months free" claim.
            let shownMonthly = displayedPrice?.amount_minor ?? 0;
            if (interval === 'yearly' && yearlyPrice !== undefined && yearlyPrice.amount_minor > 0) {
                const effectiveMonths = Math.max(1, 12 - monthsFreeOnYearly);
                shownMonthly = Math.round(yearlyPrice.amount_minor / effectiveMonths);
            }
            const subLabel = isFree
                ? 'No card needed'
                : interval === 'yearly' && yearlyPrice !== undefined
                    ? `Billed ${formatMoneyMinor(yearlyPrice.amount_minor, yearlyPrice.currency)} a year per site`
                    : monthlyPrice !== undefined && yearlyPrice !== undefined
                        ? `or ${formatMoneyMinor(yearlyPrice.amount_minor, yearlyPrice.currency)} a year per site`
                        : '';
            const cardStyle = {
                background: theme.surface,
                border: `1px solid ${featured ? theme.text : theme.border}`,
                boxShadow: featured ? `inset 0 3px 0 ${theme.lime}` : 'none',
                borderRadius: 4,
                padding: 24,
                display: 'flex',
                flexDirection: 'column',
                gap: 14,
                minWidth: 0,
            };
            return (_jsxs("article", { "aria-labelledby": `plan-${plan.slug}-name`, style: cardStyle, className: "pr-card", children: [_jsxs("header", { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }, children: [_jsx("h2", { id: `plan-${plan.slug}-name`, style: { ...serif, fontWeight: 400, fontSize: 30, lineHeight: 1, color: theme.text, margin: 0 }, children: plan.name }), featured ? (_jsx("span", { style: {
                                    ...mono,
                                    fontSize: 10,
                                    letterSpacing: 1.2,
                                    textTransform: 'uppercase',
                                    background: theme.lime,
                                    color: theme.ink,
                                    padding: '2px 6px',
                                    borderRadius: 2,
                                    whiteSpace: 'nowrap',
                                }, children: "Recommended" })) : null, showDraftBadges && plan.status === 'draft' ? (_jsx("span", { style: {
                                    ...mono,
                                    fontSize: 10,
                                    letterSpacing: 1.2,
                                    textTransform: 'uppercase',
                                    padding: '2px 6px',
                                    borderRadius: 2,
                                    border: `1px solid ${theme.amber}`,
                                    color: theme.amberFg,
                                }, children: "Draft" })) : null] }), pitch !== null ? (_jsx("div", { style: { ...sans, fontSize: 15, color: theme.text }, children: pitch })) : null, who !== undefined ? (_jsx("div", { style: { ...sans, fontSize: 13, color: theme.muted, lineHeight: 1.45 }, children: who })) : null, _jsxs("div", { style: { display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }, children: [_jsx("b", { style: { ...serif, fontWeight: 600, fontSize: 40, lineHeight: 1, color: theme.text, letterSpacing: -0.5 }, children: price === undefined ? '—' : formatPrice({ ...price, amount_minor: shownMonthly }) }), _jsx("span", { style: { ...sans, color: theme.muted, fontSize: 14 }, children: isFree ? 'forever' : 'per site a month' })] }), subLabel.length > 0 ? (_jsx("div", { style: { ...sans, fontSize: 13, color: theme.muted, marginTop: -8 }, children: subLabel })) : null, _jsx(PlanCta, { slug: plan.slug, interval: interval, location: "card", ctaHref: ctaHref, theme: theme, featured: featured, accent: isFree, label: isFree ? (planCopy?.cta ?? copy.ctaFree) : `${planCopy?.cta ?? 'Start with'} ${plan.name}` }), headline.length > 0 ? (_jsx("ul", { style: {
                            listStyle: 'none',
                            margin: 0,
                            padding: '12px 0',
                            borderTop: `1px solid ${theme.borderSoft}`,
                            borderBottom: `1px solid ${theme.borderSoft}`,
                            display: 'grid',
                            gap: 6,
                            ...sans,
                            fontSize: 14,
                            color: theme.body,
                        }, children: headline.map((r) => (_jsx("li", { style: { lineHeight: 1.45 }, children: _jsxs(Tooltip, { tip: r.tooltip, theme: theme, children: [_jsx("b", { style: { color: theme.text, fontWeight: 600 }, children: r.value }), ' ', _jsx("span", { style: { color: theme.body }, children: lowerFirst(r.label) })] }) }, r.dimension))) })) : null, featureLines.length > 0 || note !== undefined ? (_jsxs("div", { style: { display: 'grid', gap: 9, alignContent: 'start' }, children: [featureLines.length > 0 ? (_jsx("span", { style: {
                                    ...mono,
                                    fontSize: 11,
                                    letterSpacing: 1.6,
                                    textTransform: 'uppercase',
                                    color: theme.muted,
                                }, children: leadKicker })) : null, _jsx("ul", { style: { listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 9 }, children: featureLines.map((f) => (_jsxs("li", { style: { display: 'grid', gridTemplateColumns: '16px 1fr', gap: 8, alignItems: 'start' }, children: [_jsx("span", { style: { marginTop: 3, color: theme.limeFg }, children: _jsx(CheckMark, {}) }), _jsxs("span", { style: { ...sans, fontSize: 14, color: theme.body }, children: [_jsx(Tooltip, { tip: f.tooltip, theme: theme, children: f.label }), f.early ? _jsx(EarlyTag, { theme: theme }) : null] })] }, f.slug))) }), [req, note].filter((t) => t !== undefined).map((t) => (_jsx("div", { style: {
                                    ...sans,
                                    fontSize: 13,
                                    color: theme.muted,
                                    borderLeft: `2px solid ${theme.border}`,
                                    paddingLeft: 10,
                                }, children: t }, t)))] })) : null, false ? _jsx("span", { children: formatCount(1) }) : null] }, plan.plan_id));
        }) }));
}
//# sourceMappingURL=PricingCards.js.map